import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ResultStatsDto, ResultStatsRedisDto } from "@/shared/api";
import { MRT_HidedColumnDef } from "@/types/commonTypes";
import dayjs from "dayjs";
import { formatDate, formatTimestamp } from "@/lib/common-utils";
import moment from "moment/moment";
import { DATE_FORMAT_WITH_UTC_TAG } from "@/lib/constants";

export const useStatserviceTableWpcColumns = () => {
  const { t } = useTranslation();

  return useMemo<MRT_HidedColumnDef<ResultStatsRedisDto>[]>(
    () => [
      {
        id: "pid",
        header: t("statsServiceTable.pid"),
        accessorFn: (row) => row.pid,
        sortingFn: "alphanumeric",
        filterFn: "contains",
        visible: true,
      },
      {
        id: "control_type",
        header: t("statsServiceTable.control_type"),
        accessorFn: (row) => row.control_type,
        sortingFn: "alphanumeric",
        filterFn: "contains",
        visible: true,
      },
      {
        header: t("statsServiceTable.aid"),
        id: "aid",
        accessorFn: (row) => row.aid,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.room"),
        id: "room",
        accessorFn: (row) => row.room_name,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.gameType"),
        id: "game_type",
        accessorFn: (row) => row.game_type,
        sortingFn: "alphanumeric",
        filterFn: "contains",
        visible: true,
      },
      {
        header: t("statsServiceTable.common_timestamp"),
        id: "timestamp",
        accessorFn: (row) => formatTimestamp(row.common_timestamp),
        sortingFn: "alphanumeric",
        filterFn: "contains",
        visible: true,
      },
      {
        header: t("statsServiceTable.hands_count"),
        id: "hands_count",
        accessorFn: (row) => row.hands_count,
        sortingFn: "alphanumeric",
        filterFn: "contains",
        visible: true,
      },
      {
        header: t("statsServiceTable.vpip"),
        id: "vpip",
        accessorFn: (row) => row.vpip,
        sortingFn: "alphanumeric",
        filterFn: "contains",
        visible: true,
      },
      {
        header: t("statsServiceTable.pfr"),
        id: "pfr",
        accessorFn: (row) => row.pfr,
        sortingFn: "alphanumeric",
        filterFn: "contains",
        visible: true,
      },
      {
        header: t("statsServiceTable.wtsd"),
        id: "wtsd",
        accessorFn: (row) => row.wtsd,
        sortingFn: "alphanumeric",
        filterFn: "contains",
        visible: true,
      },
      {
        header: t("statsServiceTable.third_bet"),
        id: "third_bet",
        accessorFn: (row) => row.third_bet,
        sortingFn: "alphanumeric",
        filterFn: "contains",
        visible: true,
      },
      {
        header: t("statsServiceTable.open_raise_pot_bet"),
        id: "open_raise_pot_bet",
        accessorFn: (row) => row.open_raise_pot_bet,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise_05_sb"),
        id: "open_raise_05_sb",
        accessorFn: (row) => row.open_raise05_sb,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise_0667_sb"),
        id: "open_raise_0667_sb",
        accessorFn: (row) => row.open_raise0667_sb,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise10_sb"),
        id: "open_raise10_sb",
        accessorFn: (row) => row.open_raise10_sb,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise05_bb"),
        id: "open_raise05_bb",
        accessorFn: (row) => row.open_raise05_bb,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise0667_bb"),
        id: "open_raise0667_bb",
        accessorFn: (row) => row.open_raise0667_bb,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise10_bb"),
        id: "open_raise10_bb",
        accessorFn: (row) => row.open_raise10_bb,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise05_bt"),
        id: "open_raise05_bt",
        accessorFn: (row) => row.open_raise05_bt,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise0667_bt"),
        id: "open_raise0667_bt",
        accessorFn: (row) => row.open_raise0667_bt,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise10_bt"),
        id: "open_raise10_bt",
        accessorFn: (row) => row.open_raise10_bt,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise05_co"),
        id: "open_raise05_co",
        accessorFn: (row) => row.open_raise05_co,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise0667_co"),
        id: "open_raise0667_co",
        accessorFn: (row) => row.open_raise0667_co,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise10_co"),
        id: "open_raise10_co",
        accessorFn: (row) => row.open_raise10_co,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise05_mp"),
        id: "open_raise05_mp",
        accessorFn: (row) => row.open_raise05_mp,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise0667_mp"),
        id: "open_raise0667_mp",
        accessorFn: (row) => row.open_raise0667_mp,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise10_mp"),
        id: "open_raise10_mp",
        accessorFn: (row) => row.open_raise10_mp,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise05_mp1"),
        id: "open_raise05_mp1",
        accessorFn: (row) => row.open_raise05_mp1,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise0667_mp1"),
        id: "open_raise0667_mp1",
        accessorFn: (row) => row.open_raise0667_mp1,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise10_mp1"),
        id: "open_raise10_mp1",
        accessorFn: (row) => row.open_raise10_mp1,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise05_mp2"),
        id: "open_raise05_mp2",
        accessorFn: (row) => row.open_raise05_mp2,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise0667_mp2"),
        id: "open_raise0667_mp2",
        accessorFn: (row) => row.open_raise0667_mp2,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise10_mp2"),
        id: "open_raise10_mp2",
        accessorFn: (row) => row.open_raise10_mp2,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise05_utg"),
        id: "open_raise05_utg",
        accessorFn: (row) => row.open_raise05_utg,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise05_utg1"),
        id: "open_raise05_utg1",
        accessorFn: (row) => row.open_raise05_utg1,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise05_utg2"),
        id: "open_raise05_utg2",
        accessorFn: (row) => row.open_raise05_utg2,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise0667_utg"),
        id: "open_raise0667_utg",
        accessorFn: (row) => row.open_raise0667_utg,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise0667_utg1"),
        id: "open_raise0667_utg1",
        accessorFn: (row) => row.open_raise0667_utg1,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise0667_utg2"),
        id: "open_raise0667_utg2",
        accessorFn: (row) => row.open_raise0667_utg2,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise10_utg"),
        id: "open_raise10_utg",
        accessorFn: (row) => row.open_raise10_utg,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise10_utg1"),
        id: "open_raise10_utg1",
        accessorFn: (row) => row.open_raise10_utg1,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_raise10_utg2"),
        id: "open_raise10_utg2",
        accessorFn: (row) => row.open_raise10_utg2,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_limp_sb"),
        id: "open_limp_sb",
        accessorFn: (row) => row.open_limp_sb,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_limp_bb"),
        id: "open_limp_bb",
        accessorFn: (row) => row.open_limp_bb,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_limp_bt"),
        id: "open_limp_bt",
        accessorFn: (row) => row.open_limp_bt,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_limp_co"),
        id: "open_limp_co",
        accessorFn: (row) => row.open_limp_co,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_limp_mp"),
        id: "open_limp_mp",
        accessorFn: (row) => row.open_limp_mp,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_limp_mp1"),
        id: "open_limp_mp1",
        accessorFn: (row) => row.open_limp_mp1,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_limp_mp2"),
        id: "open_limp_mp2",
        accessorFn: (row) => row.open_limp_mp2,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_limp_utg"),
        id: "open_limp_utg",
        accessorFn: (row) => row.open_limp_utg,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_limp_utg1"),
        id: "open_limp_utg1",
        accessorFn: (row) => row.open_limp_utg1,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.open_limp_utg2"),
        id: "open_limp_utg2",
        accessorFn: (row) => row.open_limp_utg2,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_flop0001_to015"),
        id: "bet_flop0001_to015",
        accessorFn: (row) => row.bet_flop0001_to015,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_flop015_to029"),
        id: "bet_flop015_to029",
        accessorFn: (row) => row.bet_flop015_to029,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_flop029_to038"),
        id: "bet_flop029_to038",
        accessorFn: (row) => row.bet_flop029_to038,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_flop038_to045"),
        id: "bet_flop038_to045",
        accessorFn: (row) => row.bet_flop038_to045,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_flop045_to055"),
        id: "bet_flop045_to055",
        accessorFn: (row) => row.bet_flop045_to055,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_flop055_to062"),
        id: "bet_flop055_to062",
        accessorFn: (row) => row.bet_flop055_to062,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_flop062_to071"),
        id: "bet_flop062_to071",
        accessorFn: (row) => row.bet_flop062_to071,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_flop071_to08"),
        id: "bet_flop071_to08",
        accessorFn: (row) => row.bet_flop071_to08,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_flop08_to095"),
        id: "bet_flop08_to095",
        accessorFn: (row) => row.bet_flop08_to095,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_flop095_to105"),
        id: "bet_flop095_to105",
        accessorFn: (row) => row.bet_flop095_to105,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_flop105_to12"),
        id: "bet_flop105_to12",
        accessorFn: (row) => row.bet_flop105_to12,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_flop12_to100"),
        id: "bet_flop12_to100",
        accessorFn: (row) => row.bet_flop12_to100,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_turn0001_to015"),
        id: "bet_turn0001_to015",
        accessorFn: (row) => row.bet_turn0001_to015,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_turn015_to029"),
        id: "bet_turn015_to029",
        accessorFn: (row) => row.bet_turn015_to029,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_turn029_to038"),
        id: "bet_turn029_to038",
        accessorFn: (row) => row.bet_turn029_to038,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_turn038_to045"),
        id: "bet_turn038_to045",
        accessorFn: (row) => row.bet_turn038_to045,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_turn045_to055"),
        id: "bet_turn045_to055",
        accessorFn: (row) => row.bet_turn045_to055,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_turn055_to062"),
        id: "bet_turn055_to062",
        accessorFn: (row) => row.bet_turn055_to062,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_turn062_to071"),
        id: "bet_turn062_to071",
        accessorFn: (row) => row.bet_turn062_to071,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_turn071_to08"),
        id: "bet_turn071_to08",
        accessorFn: (row) => row.bet_turn071_to08,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_turn08_to095"),
        id: "bet_turn08_to095",
        accessorFn: (row) => row.bet_turn08_to095,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_turn095_to105"),
        id: "bet_turn095_to105",
        accessorFn: (row) => row.bet_turn095_to105,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_turn105_to12"),
        id: "bet_turn105_to12",
        accessorFn: (row) => row.bet_turn105_to12,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_turn12_to100"),
        id: "bet_turn12_to100",
        accessorFn: (row) => row.bet_turn12_to100,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_river0001_to015"),
        id: "bet_river0001_to015",
        accessorFn: (row) => row.bet_river0001_to015,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_river015_to029"),
        id: "bet_river015_to029",
        accessorFn: (row) => row.bet_river015_to029,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_river029_to038"),
        id: "bet_river029_to038",
        accessorFn: (row) => row.bet_river029_to038,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_river038_to045"),
        id: "bet_river038_to045",
        accessorFn: (row) => row.bet_river038_to045,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_river045_to055"),
        id: "bet_river045_to055",
        accessorFn: (row) => row.bet_river045_to055,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_river055_to062"),
        id: "bet_river055_to062",
        accessorFn: (row) => row.bet_river055_to062,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_river062_to071"),
        id: "bet_river062_to071",
        accessorFn: (row) => row.bet_river062_to071,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_river071_to08"),
        id: "bet_river071_to08",
        accessorFn: (row) => row.bet_river071_to08,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_river08_to095"),
        id: "bet_river08_to095",
        accessorFn: (row) => row.bet_river08_to_95,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_river095_to105"),
        id: "bet_river095_to105",
        accessorFn: (row) => row.bet_river095_to105,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_river105_to12"),
        id: "bet_river105_to12",
        accessorFn: (row) => row.bet_river105_to12,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_river12_to100"),
        id: "bet_river12_to100",
        accessorFn: (row) => row.bet_river12_to100,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.riverraise"),
        id: "riverraise",
        accessorFn: (row) => row.river_raise,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.flopraise"),
        id: "flopraise",
        accessorFn: (row) => row.flop_raise,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.turnraise"),
        id: "turnraise",
        accessorFn: (row) => row.turn_raise,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.sb_hands"),
        id: "sb_hands",
        accessorFn: (row) => row.sb_hands,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bb_hands"),
        id: "bb_hands",
        accessorFn: (row) => row.bb_hands,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.st_hands"),
        id: "st_hands",
        accessorFn: (row) => row.st_hands,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bt_hands"),
        id: "bt_hands",
        accessorFn: (row) => row.bt_hands,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.co_hands"),
        id: "co_hands",
        accessorFn: (row) => row.co_hands,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.mp_hands"),
        id: "mp_hands",
        accessorFn: (row) => row.mp_hands,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.mp1_hands"),
        id: "mp1_hands",
        accessorFn: (row) => row.mp1_hands,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.mp2_hands"),
        id: "mp2_hands",
        accessorFn: (row) => row.mp2_hands,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.utg_hands"),
        id: "utg_hands",
        accessorFn: (row) => row.utg_hands,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.utg1_hands"),
        id: "utg1_hands",
        accessorFn: (row) => row.utg1_hands,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.utg2_hands"),
        id: "utg2_hands",
        accessorFn: (row) => row.utg2_hands,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.three_bet_bar"),
        id: "three_bet_bar",
        accessorFn: (row) => row.three_bet_bar,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.wr_vs_hero"),
        id: "wr_vs_hero",
        accessorFn: (row) => row.wr_vs_hero,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.wr"),
        id: "wr",
        accessorFn: (row) => row.wr,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.win_rate"),
        id: "win_rate",
        accessorFn: (row) => row.win_rate,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.wr_bb"),
        id: "wr_bb",
        accessorFn: (row) => row.wr_bb,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.group"),
        id: "group",
        accessorFn: (row) => row.group,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_floptop_diap_001_to029"),
        id: "bet_floptop_diap_001_to029",
        accessorFn: (row) => row.bet_flop_top_diap001_to029,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.bet_flop"),
        id: "bet_flop",
        accessorFn: (row) => row.bet_flop,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.fold_flop"),
        id: "fold_flop",
        accessorFn: (row) => row.fold_flop,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.fold_turn"),
        id: "fold_turn",
        accessorFn: (row) => row.fold_turn,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.fold_river"),
        id: "fold_river",
        accessorFn: (row) => row.fold_river,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.abnormal_raise"),
        id: "abnormal_raise",
        accessorFn: (row) => row.abnormal_raise,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.wr_vs_hero_100"),
        id: "wr_vs_hero_100",
        accessorFn: (row) => row.wr_vs_hero100,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.min_raisevs_bet_flopexp"),
        id: "min_raise_vs_bet_flopexp",
        accessorFn: (row) => row.min_raise_vs_bet_flop_exp,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.min_raise_vs_bet_turn_exp"),
        id: "min_raise_vs_bet_turn_exp",
        accessorFn: (row) => row.min_raise_vs_bet_turn_exp,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.min_raise_vs_bet_riverexp"),
        id: "min_raise_vs_bet_riverexp",
        accessorFn: (row) => row.min_raise_vs_bet_river_exp,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.raise_vs_pot_bet_flop"),
        id: "raise_vs_pot_bet_flop",
        accessorFn: (row) => row.raise_vs_pot_bet_flop,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.raise_vs_pot_bet_turn"),
        id: "raise_vs_pot_bet_turn",
        accessorFn: (row) => row.raise_vs_pot_bet_turn,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.raise_vs_pot_bet_river"),
        id: "raise_vs_pot_bet_river",
        accessorFn: (row) => row.raise_vs_pot_bet_river,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.raise_vs_pot_bet_pos_flop"),
        id: "raise_vs_pot_bet_pos_flop",
        accessorFn: (row) => row.raise_vs_pot_bet_pos_flop,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.raise_vs_pot_bet_pos_turn"),
        id: "raise_vs_pot_bet_pos_turn",
        accessorFn: (row) => row.raise_vs_pot_bet_pos_turn,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.raise_vs_pot_bet_pos_river"),
        id: "raise_vs_pot_bet_pos_river",
        accessorFn: (row) => row.raise_vs_pot_bet_pos_river,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.raise_vs_pot_bet_flopcount"),
        id: "raise_vs_pot_bet_flopcount",
        accessorFn: (row) => row.raise_vs_pot_bet_flop_count,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.raise_vs_pot_bet_turn_count"),
        id: "raise_vs_pot_bet_turn_count",
        accessorFn: (row) => row.raise_vs_pot_bet_turn_count,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.raise_vs_pot_bet_river_count"),
        id: "raise_vs_pot_bet_river_count",
        accessorFn: (row) => row.raise_vs_pot_bet_river_count,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.raise_vs_pot_bet_floppos_count"),
        id: "raise_vs_pot_bet_flop_pos_count",
        accessorFn: (row) => row.raise_vs_pot_bet_flop_pos_count,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.raise_vs_pot_bet_turn_pos_count"),
        id: "raise_vs_pot_bet_turn_pos_count",
        accessorFn: (row) => row.raise_vs_pot_bet_turn_pos_count,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.raise_vs_pot_bet_river_pos_count"),
        id: "raise_vs_pot_bet_river_pos_count",
        accessorFn: (row) => row.raise_vs_pot_bet_river_pos_count,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.finger_print_01"),
        id: "finger_print_01",
        accessorFn: (row) => row.finger_print01,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.huge_open_raise"),
        id: "huge_open_raise",
        accessorFn: (row) => row.huge_open_raise,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.huge_limp_raise"),
        id: "huge_limp_raise",
        accessorFn: (row) => row.huge_limp_raise,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.huge_3bet"),
        id: "huge_3bet",
        accessorFn: (row) => row.huge_3bet,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },

      //Timing
      {
        header: t("statsServiceTable.timing.time_pre_avg_folds"),
        id: "timing.time_pre_avg_folds",
        accessorFn: (row) => row.timing?.time_pre_avg_folds,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_flop_avg_folds"),
        id: "timing.time_flop_avg_folds",
        accessorFn: (row) => row.timing?.time_flop_avg_folds,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_turn_avg_folds"),
        id: "timing.time_turn_avg_folds",
        accessorFn: (row) => row.timing?.time_turn_avg_folds,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_river_avg_folds"),
        id: "timing.time_river_avg_folds",
        accessorFn: (row) => row.timing?.time_river_avg_folds,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_pre_avg_checks"),
        id: "timing.time_pre_avg_checks",
        accessorFn: (row) => row.timing?.time_pre_avg_checks,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_flop_avg_checks"),
        id: "timing.time_flop_avg_checks",
        accessorFn: (row) => row.timing?.time_flop_avg_checks,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_turn_avg_checks"),
        id: "timing.time_turn_avg_checks",
        accessorFn: (row) => row.timing?.time_turn_avg_checks,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_river_avg_checks"),
        id: "timing.time_river_avg_checks",
        accessorFn: (row) => row.timing?.time_river_avg_checks,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_pre_avg_calls"),
        id: "timing.time_pre_avg_calls",
        accessorFn: (row) => row.timing?.time_pre_avg_calls,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_flop_avg_calls"),
        id: "timing.time_flop_avg_calls",
        accessorFn: (row) => row.timing?.time_flop_avg_calls,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_turn_avg_calls"),
        id: "timing.time_turn_avg_calls",
        accessorFn: (row) => row.timing?.time_turn_avg_calls,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_river_avg_calls"),
        id: "timing.time_river_avg_calls",
        accessorFn: (row) => row.timing?.time_river_avg_calls,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_pre_avg_raises"),
        id: "timing.time_pre_avg_raises",
        accessorFn: (row) => row.timing?.time_pre_avg_raises,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_flop_avg_raises"),
        id: "timing.time_flop_avg_raises",
        accessorFn: (row) => row.timing?.time_flop_avg_raises,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_turn_avg_raises"),
        id: "timing.time_turn_avg_raises",
        accessorFn: (row) => row.timing?.time_turn_avg_raises,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_river_avg_raises"),
        id: "timing.time_river_avg_raises",
        accessorFn: (row) => row.timing?.time_river_avg_raises,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_flop_avg_bets"),
        id: "timing.time_flop_avg_bets",
        accessorFn: (row) => row.timing?.time_flop_avg_bets,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_turn_avg_bets"),
        id: "timing.time_turn_avg_bets",
        accessorFn: (row) => row.timing?.time_turn_avg_bets,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_river_avg_bets"),
        id: "timing.time_river_avg_bets",
        accessorFn: (row) => row.timing?.time_river_avg_bets,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_pre_max_folds"),
        id: "timing.time_pre_max_folds",
        accessorFn: (row) => row.timing?.time_pre_max_folds,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_flop_max_folds"),
        id: "timing.time_flop_max_folds",
        accessorFn: (row) => row.timing?.time_flop_max_folds,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_turn_max_folds"),
        id: "timing.time_turn_max_folds",
        accessorFn: (row) => row.timing?.time_turn_max_folds,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_river_max_folds"),
        id: "timing.time_river_max_folds",
        accessorFn: (row) => row.timing?.time_river_max_folds,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_pre_max_checks"),
        id: "timing.time_pre_max_checks",
        accessorFn: (row) => row.timing?.time_pre_max_checks,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_flop_max_checks"),
        id: "timing.time_flop_max_checks",
        accessorFn: (row) => row.timing?.time_flop_max_checks,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_turn_max_checks"),
        id: "timing.time_turn_max_checks",
        accessorFn: (row) => row.timing?.time_turn_max_checks,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_river_max_checks"),
        id: "timing.time_river_max_checks",
        accessorFn: (row) => row.timing?.time_river_max_checks,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_pre_max_calls"),
        id: "timing.time_pre_max_calls",
        accessorFn: (row) => row.timing?.time_pre_max_calls,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_flop_max_calls"),
        id: "timing.time_flop_max_calls",
        accessorFn: (row) => row.timing?.time_flop_max_calls,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_turn_max_calls"),
        id: "timing.time_turn_max_calls",
        accessorFn: (row) => row.timing?.time_turn_max_calls,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_river_max_calls"),
        id: "timing.time_river_max_calls",
        accessorFn: (row) => row.timing?.time_river_max_calls,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_pre_max_raises"),
        id: "timing.time_pre_max_raises",
        accessorFn: (row) => row.timing?.time_pre_max_raises,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_flop_max_raises"),
        id: "timing.time_flop_max_raises",
        accessorFn: (row) => row.timing?.time_flop_max_raises,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_turn_max_raises"),
        id: "timing.time_turn_max_raises",
        accessorFn: (row) => row.timing?.time_turn_max_raises,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_river_max_raises"),
        id: "timing.time_river_max_raises",
        accessorFn: (row) => row.timing?.time_river_max_raises,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_flop_max_bets"),
        id: "timing.time_flop_max_bets",
        accessorFn: (row) => row.timing?.time_flop_max_bets,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_turn_max_bets"),
        id: "timing.time_turn_max_bets",
        accessorFn: (row) => row.timing?.time_turn_max_bets,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_river_max_bets"),
        id: "timing.time_river_max_bets",
        accessorFn: (row) => row.timing?.time_river_max_bets,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_pre_min_checks"),
        id: "timing.time_pre_min_checks",
        accessorFn: (row) => row.timing?.time_pre_min_checks,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_flop_min_checks"),
        id: "timing.time_flop_min_checks",
        accessorFn: (row) => row.timing?.time_flop_min_checks,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_turn_min_checks"),
        id: "timing.time_turn_min_checks",
        accessorFn: (row) => row.timing?.time_turn_min_checks,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_river_min_checks"),
        id: "timing.time_river_min_checks",
        accessorFn: (row) => row.timing?.time_river_min_checks,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_pre_min_calls"),
        id: "timing.time_pre_min_calls",
        accessorFn: (row) => row.timing?.time_pre_min_calls,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_flop_min_calls"),
        id: "timing.time_flop_min_calls",
        accessorFn: (row) => row.timing?.time_flop_min_calls,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_turn_min_calls"),
        id: "timing.time_turn_min_calls",
        accessorFn: (row) => row.timing?.time_turn_min_calls,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_river_min_calls"),
        id: "timing.time_river_min_calls",
        accessorFn: (row) => row.timing?.time_river_min_calls,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_pre_min_raises"),
        id: "timing.time_pre_min_raises",
        accessorFn: (row) => row.timing?.time_pre_min_raises,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_flop_min_raises"),
        id: "timing.time_flop_min_raises",
        accessorFn: (row) => row.timing?.time_flop_min_raises,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_turn_min_raises"),
        id: "timing.time_turn_min_raises",
        accessorFn: (row) => row.timing?.time_turn_min_raises,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_river_min_raises"),
        id: "timing.time_river_min_raises",
        accessorFn: (row) => row.timing?.time_river_min_raises,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_flop_min_bets"),
        id: "timing.time_flop_min_bets",
        accessorFn: (row) => row.timing?.time_flop_min_bets,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_turn_min_bets"),
        id: "timing.time_turn_min_bets",
        accessorFn: (row) => row.timing?.time_turn_min_bets,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_river_min_bets"),
        id: "timing.time_river_min_bets",
        accessorFn: (row) => row.timing?.time_river_min_bets,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_pre_min_folds"),
        id: "timing.time_pre_min_folds",
        accessorFn: (row) => row.timing?.time_pre_min_folds,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_flop_min_folds"),
        id: "timing.time_flop_min_folds",
        accessorFn: (row) => row.timing?.time_flop_min_folds,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_turn_min_folds"),
        id: "timing.time_turn_min_folds",
        accessorFn: (row) => row.timing?.time_turn_min_folds,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_river_min_folds"),
        id: "timing.time_river_min_folds",
        accessorFn: (row) => row.timing?.time_river_min_folds,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_pre_first_folds"),
        id: "timing.time_pre_first_folds",
        accessorFn: (row) => row.timing?.time_pre_first_folds,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_flop_first_folds"),
        id: "timing.time_flop_first_folds",
        accessorFn: (row) => row.timing?.time_flop_first_folds,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_turn_first_folds"),
        id: "timing.time_turn_first_folds",
        accessorFn: (row) => row.timing?.time_turn_first_folds,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_river_first_folds"),
        id: "timing.time_river_first_folds",
        accessorFn: (row) => row.timing?.time_river_first_folds,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_pre_first_checks"),
        id: "timing.time_pre_first_checks",
        accessorFn: (row) => row.timing?.time_pre_first_checks,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_flop_first_checks"),
        id: "timing.time_flop_first_checks",
        accessorFn: (row) => row.timing?.time_flop_first_checks,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_turn_first_checks"),
        id: "timing.time_turn_first_checks",
        accessorFn: (row) => row.timing?.time_turn_first_checks,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_river_first_checks"),
        id: "timing.time_river_first_checks",
        accessorFn: (row) => row.timing?.time_river_first_checks,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_pre_first_bets"),
        id: "timing.time_pre_first_bets",
        accessorFn: (row) => row.timing?.time_pre_first_bets,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_flop_first_bets"),
        id: "timing.time_flop_first_bets",
        accessorFn: (row) => row.timing?.time_flop_first_bets,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_turn_first_bets"),
        id: "timing.time_turn_first_bets",
        accessorFn: (row) => row.timing?.time_turn_first_bets,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
      {
        header: t("statsServiceTable.timing.time_river_first_bets"),
        id: "timing.time_river_first_bets",
        accessorFn: (row) => row.timing?.time_river_first_bets,
        sortingFn: "alphanumeric",
        filterFn: "contains",
      },
    ],
    [t]
  );
};

